/* eslint-disable no-console */
import {Platform} from 'react-native';
import * as KeyChain from 'react-native-keychain';

import FaceIDModal from '/component/partial/FaceIDModal/FaceIDModal';
import {PinModal} from '/component/partial/PinModal/PinModal';
import {getErrorTrackingUtilDefaultInstance} from '/util/error-tracking/error-tracking.util';

import {checkPin, handlePinLogin, handleTurnOffPin} from './pinCore.util';

export const handleTurnOnPinSkipButton =
  ({setPinOptInValue, setPinOptIn, hidePopup}: any) =>
  () => {
    return setPinOptInValue(false)
      .then(() => setPinOptIn(false))
      .then(() => hidePopup());
  };
export const handleNotnowButton =
  ({
    setBiometricsOptInValue,
    setBiometricsOptIn,
    hidePopup,
    biometricsOptIn,
  }: any) =>
  () => {
    return setBiometricsOptInValue(biometricsOptIn)
      .then(() => setBiometricsOptIn(biometricsOptIn))
      .then(() => hidePopup());
  };
export const handleTurnOffPinSkipButton =
  ({setPinOptInValue, setPinOptIn, hidePopup}: any) =>
  () => {
    return setPinOptInValue(true)
      .then(() => setPinOptIn(true))
      .then(() => hidePopup());
  };

export const handleTurnOffBiometricsSkipButton =
  ({setBiometricsOptInValue, setBiometricsOptIn, hidePopup}: any) =>
  () => {
    return setBiometricsOptInValue(true)
      .then(() => setBiometricsOptIn(true))
      .then(() => hidePopup());
  };

export const turnOnPin =
  ({showPopup, setPinOptInValue, setPinOptIn, hidePopup}: any) =>
  () => {
    showPopup(
      PinModal,
      {
        showCreatePinModal: true,
        skipButton: () =>
          handleTurnOnPinSkipButton({
            setPinOptInValue,
            setPinOptIn,
            hidePopup,
          })(),
        onClose: hidePopup,
      },
      {avoidable: false, showFullModal: true},
    );
  };

export const turnOnBiometrics =
  ({
    setBiometricsOptInValue,
    biometricsOptIn,
    setBiometricsOptIn,
    hidePopup,
    showPopup,
    setbiometricsWasRequested,
  }: any) =>
  async () => {
    async function isBiometricEnabled(): Promise<boolean> {
      try {
        setbiometricsWasRequested(true);
        // Attempt to retrieve a password using biometrics
        const credentials = await KeyChain.getGenericPassword({
          service: 'connect-app-biometrics-login',
        });
        if (credentials) {
          await setbiometricsWasRequested(true);
          return true;
        } else {
          return false;
        }
      } catch (error: any) {
        // Handle cases where biometrics are disabled or unavailable
        if (
          error.message.includes('User canceled the operation') ||
          error.message.includes('Not available')
        ) {
          getErrorTrackingUtilDefaultInstance().trackError(
            error,
            `Biometrics are disabled or unavailable:, ${error.message}`,
          );
          return false;
        }

        getErrorTrackingUtilDefaultInstance().trackError(
          error,
          `Biometricsunexpected error occurred:, ${error.message}`,
        );
        return false;
      }
    }

    KeyChain.getSupportedBiometryType()
      .then(resultObject => {
        // Handle the case where no biometric support is available
        if (resultObject === null) {
          KeyChain.getGenericPassword({
            service: 'connect-app-biometrics-login',
          });
          isBiometricEnabled().then(enabled => {
            setBiometricsOptIn(enabled);
            setBiometricsOptInValue(enabled);
            if (Platform.OS === 'ios') {
              showPopup(
                FaceIDModal,
                {
                  NotnowButton: () =>
                    handleNotnowButton({
                      setBiometricsOptInValue,
                      setBiometricsOptIn,
                      hidePopup,
                      biometricsOptIn,
                    })(),
                  onClose: hidePopup,
                  biometricsOptIn: biometricsOptIn,
                },
                {avoidable: false, showFullModal: true},
              );
            }
          });

          return;
        }
        // Handle the case where FaceID is supported and biometrics are not yet opted-in
        if (
          (resultObject === 'FaceID' ||
            resultObject === 'TouchID' ||
            resultObject === 'Fingerprint') &&
          !biometricsOptIn
        ) {
          isBiometricEnabled()
            .then(enabled => {
              setBiometricsOptIn(enabled);
              setBiometricsOptInValue(enabled);
            })
            .catch(err =>
              console.error('Error checking biometric enabled:', err),
            );

          return;
        }
        setBiometricsOptInValue(true);
        setBiometricsOptIn(true);
      })
      .catch(error => {
        console.error('Error during biometric check:', error);
      });
  };

export const turnOffBiometricsSwitch =
  ({
    setBiometricsOptInValue,
    biometricsOptIn,
    setBiometricsOptIn,
    showPopup,
    hidePopup,
  }: any) =>
  async () => {
    // setBiometricsOptInValue(!biometricsOptIn);
    // setBiometricsOptIn(!biometricsOptIn);

    KeyChain.getSupportedBiometryType().then(resultObject => {
      // Handle the case where no biometric support is available
      if (resultObject === null) {
        if (Platform.OS === 'ios') {
          showPopup(
            FaceIDModal,
            {
              NotnowButton: () =>
                handleNotnowButton({
                  setBiometricsOptInValue,
                  setBiometricsOptIn,
                  hidePopup,
                  biometricsOptIn,
                })(),
              onClose: hidePopup,
              biometricsOptIn: biometricsOptIn,
            },
            {avoidable: false, showFullModal: true},
          );
        }
        setBiometricsOptIn(false);
        setBiometricsOptInValue(false);
      } else {
        if (Platform.OS === 'ios') {
          showPopup(
            FaceIDModal,
            {
              NotnowButton: () =>
                handleNotnowButton({
                  setBiometricsOptInValue,
                  setBiometricsOptIn,
                  hidePopup,
                  biometricsOptIn,
                })(),
              onClose: hidePopup,
              biometricsOptIn: biometricsOptIn,
            },
            {avoidable: false, showFullModal: true},
          );
        }
        setBiometricsOptIn(true);
        setBiometricsOptInValue(true);
      }
    });
  };

export const turnOffPin =
  ({
    showPopup,
    setPinOptInValue,
    setPinOptIn,
    hidePopup,
    getUserPinValue,
  }: any) =>
  () => {
    showPopup(
      PinModal,
      {
        showCreatePinModal: false,
        skipButton: () =>
          handleTurnOffPinSkipButton({
            setPinOptInValue,
            setPinOptIn,
            hidePopup,
          })(),
        onClose: hidePopup,
        onSubmit: (pin: any) =>
          handleTurnOffPin({
            pin,
            getUserPinValue,
            hidePopup,
            setPinOptInValue,
            setPinOptIn,
          })(),
      },
      {avoidable: false, showFullModal: true},
    );
  };

export const openFaceID =
  ({showPopup}: any) =>
  () => {
    showPopup(FaceIDModal, {avoidable: false, showFullModal: true});
  };

export const changePin =
  ({showPopup, hidePopup}: any) =>
  () => {
    showPopup(
      PinModal,
      {
        showCreatePinModal: true,
        skipButton: hidePopup,
        onClose: hidePopup,
      },
      {avoidable: false, showFullModal: true},
    );
  };

export const handlePinFallback =
  ({showPopup, hidePopup, getUserPinValue, setValue, handleOnSubmit}: any) =>
  () => {
    showPopup(
      PinModal,
      {
        showCreatePinModal: false,
        skipButton: hidePopup,
        onSubmit: (pin: any) =>
          checkPin({
            pin,
            getUserPinValue,
            hidePopup,
            handlePinLogin,
            setValue,
            handleOnSubmit,
          })(),
      },
      {avoidable: false, showFullModal: true},
    );
  };

export const handleInitBiometricsAndPin =
  ({setInitBiometricsAndPin, hidePopup}: any) =>
  () => {
    return setInitBiometricsAndPin(true).then(hidePopup);
  };

export const handleInitBiometrics =
  ({setBiometricsOptInValue, hidePopup}: any) =>
  () => {
    return setBiometricsOptInValue(true).then(hidePopup);
  };

export const handleSkipInitBiometricsAndPin =
  ({
    setInitBiometricsAndPin,
    hidePopup,
    setBiometricsOptInValue,
    setPinOptInValue,
  }: any) =>
  () => {
    return setInitBiometricsAndPin(false)
      .then(() => setBiometricsOptInValue(false))
      .then(() => setPinOptInValue(false))
      .then(hidePopup);
  };
export const handleSkipAddPinToBiometricsLogin =
  ({setBiometricsOptInValue, hidePopup}: any) =>
  () => {
    return setBiometricsOptInValue(false).then(hidePopup);
  };
